import {
    EzObject,
    EzBoolean,
    EzString,
    EzHtml,
    EzJson
} from '/ezlibrary/helpers/EzHelpers.js';

import {
    EzRegistrationState,
    EzElementEventName,
    EzValidationResponseStatus
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzClass } from '/ezlibrary/EzClass.js';

import { EzAccountServices } from '/public/javascript/services/ezclocker-account-services.js';

import { EzFormat } from '/public/javascript/common/ez-format.js';
import { EzTickerTimer } from '/public/javascript/common/ez-ticker-timer.js';
import { EzDialogConfig } from '/ezlibrary/ux/dialogs/EzDialogConfig.js';
import { EzSpinner } from '/public/webcomponents/spinner/EzSpinner.js';
import { EzUx } from '/ezlibrary/ux/EzUx.js';
import { EzDialog } from '/public/javascript/common/ezclocker-dialog-helper.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Provides functionality to request a password reset.
 * ---------------------------------------------------------------------------
 * Import with:
 *     import { EzRequestPasswordResetDialog } from '/public/webcomponents/EzRequestPasswordResetDialog/ez-request-password-reset-dialog.js';
 * ---------------------------------------------------------------------------
 * Singleton instance ready for use when the below evaluates to true:
 *     globalThis?.ezApi?.ezclocker?.[EzRequestPasswordResetDialog.ezApiName]?.ready
 * ---------------------------------------------------------------------------
 * Listen to onReady event:
 *     EzClass.ezWaitReady(
 *         EzRequestPasswordResetDialog.ezEventNames.onReady,
 *         {listening_class}.#ezRegistrator);
 * ---------------------------------------------------------------------------
 * Singleton Instance Reference:
 *     Inside this class: EzRequestPasswordResetDialog.ezInstance
 *     Outside this class: globalThis.ezApi.ezclocker.ezRequestPasswordResetDialog
 * ---------------------------------------------------------------------------
 */
export class EzRequestPasswordResetDialog extends EzClass {
    /**
     * @public @readonly @property
     * Returns the root Element's id for this dialog.
     * @returns {string}
     */
    get ezDialogId() {
        return 'EzRequestPasswordResetDialog';
    }

    /**
     * @public @readonly @property
     * Returns an object of HTML element names to ids
     * @returns {object}
     */
    get ezIds() {
        return {
            ezResetRequestInputViewId: 'ezOn_EzRequestPasswordResetDialog_ResetRequestsInputView',
            ezErrorBoxId: 'ezOn_EzRequestPasswordResetDialog_ErrorBox',
            ezWarningBoxId: 'ezOn_EzRequestPasswordResetDialog_WarningBox',
            inputs: {
                ezEmailOrUserNameInputId: 'ezOn_EzRequestPasswordResetDialog_EmailOrUserName',
                ezMobilePhoneNumberInputId: 'ezOn_EzRequestPasswordResetDialog_MobilePhoneNumber'
            },
            buttons: {
                ezOKButtonId: 'ezOn_EzRequestPasswordResetDialog_OKBtn',
                ezResetPasswordButtonId: 'ezOn_EzRequestPasswordResetDialog}_RequestdBtn',
                ezCancelButtonId: 'ezOn_EzRequestPasswordResetDialog_CancelBtn'
            }
        };
    }

    /**
     * @public @function
     * Initializes EzRequestPasswordResetDialog
     * @returns {EzRequestPasswordResetDialog}
     */
    ezInit() {
        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzRequestPasswordResetDialog.ezApiName,
            EzRequestPasswordResetDialog.ezEventNames.onResetPasswordSubmitted);

        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzRequestPasswordResetDialog.ezApiName,
            EzRequestPasswordResetDialog.ezEventNames.onResetPasswordCanceled);

        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzRequestPasswordResetDialog.ezApiName,
            EzRequestPasswordResetDialog.ezEventNames.onResetPasswordError);

        EzRequestPasswordResetDialog.ezInstance.ezInitUX();

        return EzRequestPasswordResetDialog.ezInstance;
    }

    /**
     * @public @function
     * Initializes the dialogs UX
     */
    ezInitUX() {
        return EzRequestPasswordResetDialog.ezInstance;
    }

    /**
     * @public @function
     * Resets the dialog to it's initial state
     */
    ezResetDialog() {
        if (EzUx.elementExists("EzHiddenRestPasswordRequestDialogContainer")) {
            globalThis.ezApi.ezclocker.ezEventEngine.ezUnwantElementEvent(
                EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId,
                EzElementEventName.BLUR,
                EzRequestPasswordResetDialog.ezApiName);

            globalThis.ezApi.ezclocker.ezEventEngine.ezUnwantElementEvent(
                EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId,
                EzElementEventName.BLUR,
                EzRequestPasswordResetDialog.ezApiName);

            EzUx.removeElement("EzHiddenRestPasswordRequestDialogContainer");
        }

        EzUx.appendContent(
            'body',
            EzHtml.build`
                <div
                    id="EzHiddenRestPasswordRequestDialogContainer"
                    style="display:none">
                    ${EzRequestPasswordResetDialog.ezInstance.ezBuildResetPasswordRequestDialogHtml()}
                </div>`);

        let ezDialogConfig = new EzDialogConfig(EzRequestPasswordResetDialog.ezInstance.ezDialogId);

        ezDialogConfig.width = 600;

        ezDialogConfig.title = 'Forgot Password';

        ezDialogConfig.buttons = [
            {
                id: EzRequestPasswordResetDialog.ezInstance.ezIds.buttons.ezOKButtonId,
                text: 'OK',
                click: EzRequestPasswordResetDialog.ezInstance.ezClose
            },
            {
                id: EzRequestPasswordResetDialog.ezInstance.ezIds.buttons.ezResetPasswordButtonId,
                text: 'Reset My Password',
                click: EzRequestPasswordResetDialog.ezInstance.ezSubmit
            },
            {
                id: EzRequestPasswordResetDialog.ezInstance.ezIds.buttons.ezCancelButtonId,
                text: 'Cancel',
                click: EzRequestPasswordResetDialog.ezInstance.ezCancel
            }
        ];

        globalThis.ezApi.ezclocker.ezDialog.ezInitDialogWithConfig(
            EzRequestPasswordResetDialog.ezInstance.ezDialogId,
            ezDialogConfig);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId,
            EzElementEventName.BLUR,
            EzRequestPasswordResetDialog.ezApiName,
            () => EzRequestPasswordResetDialog.ezInstance.ezValidateInputs(false));

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId,
            EzElementEventName.BLUR,
            EzRequestPasswordResetDialog.ezApiName,
            () => EzRequestPasswordResetDialog.ezInstance.ezValidateInputs(false));

        // Hide confirmation elements and error boxes
        EzUx.hide(EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId);

        EzUx.hide(EzRequestPasswordResetDialog.ezInstance.ezIds.ezWarningBoxId);

        EzUx.hide(EzRequestPasswordResetDialog.ezInstance.ezIds.buttons.ezOKButtonId);

        // Show initial elements
        EzUx.show(EzRequestPasswordResetDialog.ezInstance.ezIds.ezResetRequestInputViewId);

        EzUx.show(EzRequestPasswordResetDialog.ezInstance.ezIds.buttons.ezResetPasswordButtonId);

        EzUx.show(EzRequestPasswordResetDialog.ezInstance.ezIds.buttons.ezCancelButtonId);

        // Inputs
        EzUx.hideInputError(EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId);

        EzUx.hideInputError(EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId);

        EzUx.hideInputWarning(EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId);

        EzUx.hideInputWarning(EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId);

        EzUx.setInputValue(
            EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId,
            EzString.EMPTY);

        EzUx.setInputValue(
            EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId,
            EzString.EMPTY);
    }

    /**
     * @public @function
     * Shows the dialog
     */
    ezShow(email) {
        EzRequestPasswordResetDialog.ezInstance.ezResetDialog();

        if (EzString.hasText(email)) {
            EzUx.setInputValue(
                EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId,
                email.trim());

            EzUx.focus(EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId);

            EzUx.selectAll(EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId);

            EzRequestPasswordResetDialog.ezInstance.ezValidateInputs();
        } else {
            EzUx.focus(EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId);
        }

        globalThis.ezApi.ezclocker.ezDialog.ezShowDialog(EzRequestPasswordResetDialog.ezInstance.ezDialogId);
    }

    /**
     * @public @function
     * Closes the password reset view without submitting. Does NOT call the onCancel callback.
     */
    ezClose() {
        globalThis.ezApi.ezclocker.ezDialog.ezCloseDialog(EzRequestPasswordResetDialog.ezInstance.ezDialogId);
    }

    /**
     * @public @function
     * Cancels the dialog and closes the dialog. Calls the onCancel callback.
     */
    ezCancel() {
        EzRequestPasswordResetDialog.ezInstance.ezClose();

        globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
            EzRequestPasswordResetDialog.ezEventNames.onResetPasswordCanceled,
            EzRequestPasswordResetDialog.ezInstance);
    }

    /**
     * @public @function
     * Submits the password reset request
     */
    ezSubmit() {
        if (!EzRequestPasswordResetDialog.ezInstance.ezValidateInputs()) {
            return;
        }

        EzRequestPasswordResetDialog.ezInstance.ezClose();

        EzUx.hide(EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId);

        EzUx.hide(EzRequestPasswordResetDialog.ezInstance.ezIds.ezWarningBoxId);

        let emailAddress = EzRequestPasswordResetDialog.ezInstance.ezGetEmailOrUserName();

        let phone = EzRequestPasswordResetDialog.ezInstance.ezGetMobilePhoneNumber();

        let userNameToUse = EzString.hasText(emailAddress)
            ? emailAddress
            : phone;

        return globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitAsync(
            'Requesting password reset ...',
            (finished, waitDone) => globalThis.ezApi.ezclocker.ezAccountServices.ezRequestPasswordReset(userNameToUse, emailAddress, phone)
                .then(
                    () => {
                        waitDone();

                        return globalThis.ezApi.ezclocker.ezDialog.ezShowOK(
                            'Password Reset Requested',
                            EzString.msg`
                                If we find an account that matches the email address/user name or mobile phone number you entered we will send either
                                a email or text message with the reset password link.`)
                            .then(
                                () => {
                                    globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                                        EzRequestPasswordResetDialog.ezEventNames.onResetPasswordSubmitted,
                                        EzRequestPasswordResetDialog.ezInstance);

                                    return finished();
                                });
                    },
                    (eResponse) => {
                        waitDone();

                        let em = EzString.em`
                            EzClocker is unable to reset your password at this time.
                            Please contact support at
                            <a href="mailto:support@ezclocker.com?subject='Help reset password'>
                                support@ezclocker.com
                            </a>.`;

                        let details = EzString.em`
                            [Error Response]\n
                            ${EzJson.toJson(eResponse)}`;

                        return globalThis.ezApi.ezclocker.ezDialog.ezShowErrorWithDetails(
                            'Error During Password Reset',
                            em,
                            details)
                            .then(
                                () => {
                                    globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                                        EzRequestPasswordResetDialog.ezEventNames.onResetPasswordError,
                                        globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                                            EzRequestPasswordResetDialog.ezApiName,
                                            em,
                                            eResponse));

                                    return finished();
                                });

                    }));
    }

    /**
     * @public @function
     * Validates that the user has enterd enough information to request a password reset.
     * @param {boolean} [errorBlank=true]
     * When true, an error is shown if both email and phone number are blank.
     * @return {boolean}
     */
    ezValidateInputs(errorBlank = true) {
        EzUx.hide(EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId);

        EzUx.setContent(EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId, EzString.EMPTY);

        EzUx.hide(EzRequestPasswordResetDialog.ezInstance.ezIds.ezWarningBoxId);

        EzUx.setContent(EzRequestPasswordResetDialog.ezInstance.ezIds.ezWarningBoxId, EzString.EMPTY);

        const email = EzRequestPasswordResetDialog.ezInstance.ezGetEmailOrUserName();

        const phone = EzRequestPasswordResetDialog.ezInstance.ezGetMobilePhoneNumber();

        if (EzBoolean.isTrue(errorBlank) && !EzString.hasText(email) && !EzString.hasText(phone)) {
            EzUx.setContent(
                EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId,
                'Enter a valid email address OR mobile phone number.');

            EzUx.show(EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId);

            EzUx.disable(EzRequestPasswordResetDialog.ezInstance.ezIds.buttons.ezOKButtonId);

            return false;
        }

        if (EzString.hasText(email)) {
            const vResponse = globalThis.ezApi.ezclocker.ezValidation.ezValidateEmailAddressInput(
                EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId,
                EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId);

            if (EzValidationResponseStatus.ERROR === vResponse.ezGetStatus()) {
                EzUx.setContent(
                    EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId,
                    vResponse.message);

                EzUx.show(EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId);

                EzUx.disable(EzRequestPasswordResetDialog.ezInstance.ezIds.buttons.ezOKButtonId);

                return false;
            }
        }

        if (EzString.hasText(phone)) {
            const vResponse = globalThis.ezApi.ezclocker.ezValidation.ezValidatePhoneNumberInput(
                EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId,
                false,
                EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId,
                EzRequestPasswordResetDialog.ezInstance.ezIds.ezWarningBoxId);

            if (EzValidationResponseStatus.ERROR === vResponse.ezGetStatus()) {
                EzUx.setContent(
                    EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId,
                    vResponse.message);

                EzUx.show(EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId);

                EzUx.disable(EzRequestPasswordResetDialog.ezInstance.ezIds.buttons.ezOKButtonId);

                return false;
            }
        }

        EzUx.enable(EzRequestPasswordResetDialog.ezInstance.ezIds.buttons.ezOKButtonId);

        return true;
    }

    /**
     * @public @function
     * Returns the email address or user name entered by the user.
     * @returns {string}
     */
    ezGetEmailOrUserName() {
        return EzString.stringOrEmpty(
            EzUx.getInputValue(
                EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId)?.trim());
    }

    /**
     * @public @function
     * Returns the mobile phone number entered by the user.
     * @returns {string}
     */
    ezGetMobilePhoneNumber() {
        let phoneNumber = EzUx.getInputValue(
            EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId);

        if (!ezApi.ezclocker.ezFormat.ezIsUSPhoneNumber(phoneNumber)) {
            phoneNumber = EzString.hasLength(phoneNumber)
                ? globalThis.ezApi.ezclocker.ezFormat.ezFormatUSPhoneNumber(phoneNumber)
                : EzString.EMPTY;

            // Update the value in the editor
            EzUx.setInputValue(
                EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId,
                phoneNumber);
        }

        return phoneNumber;
    }

    /**
     * @public @function
     * Builds the dialog's HTML
     * @returns {string}
     */
    ezBuildResetPasswordRequestDialogHtml() {
        return EzHtml.build`
            <div
                id="${EzRequestPasswordResetDialog.ezInstance.ezDialogId}"
                title="Request Password Reset">
                <style>
                    .ez-request-password-reset-view {
                    }
                    .ez-request-password-reset-confirmation-view {
                    }
                </style>
                <div
                    id="${EzRequestPasswordResetDialog.ezInstance.ezIds.ezResetRequestInputViewId}"
                    class="ez-request-password-reset-view">
                    <p>
                        To reset your password, enter either the email address/user name you use to log into ezclocker
                        or the mobile phone number associated with your account.
                    </p>
                    <div
                        class="ezInputBox">
                        <label
                            id="${EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId}_Label"
                            for="${EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId}"
                            class="ezBold">
                            Email Address or User Name
                        </label>
                        <input
                            id="${EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezEmailOrUserNameInputId}"
                            type="text"
                            class="ezFullWidthEditor"
                            autocomplete="username"
                            name="emailOrUserName"/>
                    </div>
                    <div
                        class="ezCenterInputBox">
                        <label
                            class="ezBold">
                            - OR -
                        </label>
                    </div>
                    <div
                        class="ezInputBox">
                        <label
                            id="${EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId}Label"
                            for="${EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId}"
                            class="ezBold">
                            Mobile Phone Number
                        </label>
                        <input
                            id="${EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId}"
                            type="text"
                            class="ezFullWidthEditor"
                            title="Format: 555-555-5555"
                            name="mobilephone"
                            autocomplete="tel"/>
                        <label
                            id="${EzRequestPasswordResetDialog.ezInstance.ezIds.inputs.ezMobilePhoneNumberInputId}_FormatHint"
                            class="ezInputFormatLabel">
                            Format: 555-555-5555
                        </label>
                    </div>
                </div>
                <div
                    id="${EzRequestPasswordResetDialog.ezInstance.ezIds.ezErrorBoxId}"
                    class="ezErrorBox"
                    style="display:none">
                </div>
                <div
                    id="${EzRequestPasswordResetDialog.ezInstance.ezIds.ezWarningBoxId}"
                    class="ezWarningBox"
                    style="display:none">
                </div>
            </div>`;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Returns the name of this class's singleton instance when registered with globalThis.ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezRequestPasswordResetDialog';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzRequestPasswordResetDialog_Ready',
            onResetPasswordSubmitted: 'EzRequestPasswordResetDialog_OnSubmitted',
            onResetPasswordCanceled: 'EzRequestPasswordResetDialog_OnCanceled',
            onResetPasswordError: 'EzRequestPasswordResetDialog_OnError'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with globalThis.ezApi.
     * @type {EzRequestPasswordResetDialog}
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzRequestPasswordResetDialog.ezApiName]
        ? globalThis.ezApi.ezclocker[EzRequestPasswordResetDialog.ezApiName]
        : null;

    /**
     * @static
     * @public @property @getter
     * Returns the singleton instance of this class that is registered with EzApi (if available yet)
     * @returns {EzRequestPasswordResetDialog}
     */
    static get ezInstance() {
        return EzRequestPasswordResetDialog.#ezInstance;
    }

    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with globalThis.ezApi.
     * @param {EzRequestPasswordResetDialog} ezInstance
     */
    static set ezInstance(ezInstance) {
        if (null != EzRequestPasswordResetDialog.#ezInstance) {
            throw new Error('EzRequestPasswordResetDialog\'s singleton instance is already reigstered with globalThis.ezApi.');
        }

        EzRequestPasswordResetDialog.#ezInstance = ezInstance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzRequestPasswordResetDialog.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;

    /**
     * @static
     * @public @property @getter
     * Returns the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzRequestPasswordResetDialog.#ezApiRegistrationState;
    }

    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzRequestPasswordResetDialog.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Returns true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzRequestPasswordResetDialog.ezApiRegistrationState && globalThis?.ezApi?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzAccountServices.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzFormat.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzTickerTimer.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzSpinner.ezApiName]?.ready &&
            globalThis?.ezApi?.ezclocker?.[EzDialog.ezApiName]?.ready;

    }
    /**
     * @static
     * @private @readonly @property
     * Returns if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzRequestPasswordResetDialog.ezInstance &&
            EzRegistrationState.REGISTERED === EzRequestPasswordResetDialog.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @function
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzRequestPasswordResetDialog.#ezCanRegister && !EzRequestPasswordResetDialog.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzRequestPasswordResetDialog, EzRequestPasswordResetDialog.ezApiName);
        }

        return EzRequestPasswordResetDialog.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzRequestPasswordResetDialog.ezApiName
     *     2) Property getter EzRequestPasswordResetDialog.ezEventNames
     *     3) Property getter EzRequestPasswordResetDialog.ezInstance
     *     4) Property setter EzRequestPasswordResetDialog.ezInstance
     *     5) Property getter EzRequestPasswordResetDialog.ezApiRegistrationState
     *     6) Property setter EzRequestPasswordResetDialog.ezApiRegistrationState
     *     7) Property getter EzRequestPasswordResetDialog.#ezCanRegister
     *     8) Property getter EzRequestPasswordResetDialog.#ezIsRegistered
     *     9) Method EzRequestPasswordResetDialog.#ezRegistrator()
     */
    static {
        if (!EzRequestPasswordResetDialog.#ezIsRegistered) {
            EzRequestPasswordResetDialog.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzRequestPasswordResetDialog.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzClass.ezOnEzApiReadyEventName,
                    EzRequestPasswordResetDialog.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzAccountServices.ezEventNames.onReady,
                    EzRequestPasswordResetDialog.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzFormat.ezEventNames.onReady,
                    EzRequestPasswordResetDialog.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzTickerTimer.ezEventNames.onReady,
                    EzRequestPasswordResetDialog.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzSpinner.ezEventNames.onReady,
                    EzRequestPasswordResetDialog.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzDialog.ezEventNames.onReady,
                    EzRequestPasswordResetDialog.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
